import { useMemo, useState } from 'react';
import { KanbanColumn } from '@/components/tracker/KanbanColumn';
import { TrackerListRow } from '@/components/tracker/TrackerListRow';
import type {
  KanbanBucketKey,
  KanbanBuckets,
  KanbanColumnDef,
  TrackerView,
} from '@/components/tracker/tracker.types';
import { ViewToggle } from '@/components/tracker/ViewToggle';
import { useJobs } from '@/hooks/useJobs';
import { useSelectJob } from '@/providers/dashboard/JobBrowserContext';
import type { Job } from '@/types/job.type';

const KANBAN_COLUMNS: readonly KanbanColumnDef[] = [
  { hint: 'Tailored & waiting on you', id: 'pending', label: 'Pending review' },
  { hint: 'Ready to submit', id: 'approved', label: 'Approved' },
  { hint: 'Out in the wild', id: 'submitted', label: 'Submitted' },
  { hint: 'Closed out', id: 'rejected', label: 'Rejected' },
] as const;

const bucketForJob = (job: Job): KanbanBucketKey => {
  if (job.appStatus === 'rejected' || job.status === 'rejected') {
    return 'rejected';
  }

  if (job.appStatus === 'submitted' || job.status === 'applied') {
    return 'submitted';
  }

  if (job.appStatus === 'approved') {
    return 'approved';
  }

  return 'pending';
};

export const Tracker = () => {
  const jobs = useJobs();
  const select = useSelectJob();
  const [view, setView] = useState<TrackerView>('kanban');

  const apps = useMemo(
    () =>
      jobs.filter(
        ({ applicationId, status }) =>
          applicationId != null
          || status === 'ready'
          || status === 'approved'
          || status === 'applied'
          || status === 'rejected',
      ),
    [jobs],
  );

  const byColumn = useMemo<KanbanBuckets>(() => {
    const buckets: KanbanBuckets = {
      approved: [],
      pending: [],
      rejected: [],
      submitted: [],
    };

    for (const job of apps) {
      buckets[bucketForJob(job)].push(job);
    }

    return buckets;
  }, [apps]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between border-b border-border bg-surface/30 px-5 py-2.5">
        <div className="flex items-center gap-3">
          <h2 className="text-sm lg:text-base font-medium">
            Application tracker
          </h2>
          <span className="font-mono text-xs lg:text-sm text-subtle tabular-nums">
            {apps.length} total · {byColumn.submitted.length} submitted ·{' '}
            {byColumn.pending.length} pending
          </span>
        </div>
        <ViewToggle onChange={setView} view={view} />
      </div>

      {view === 'kanban' ? (
        <div className="min-h-0 flex-1 overflow-x-auto overflow-y-hidden p-5">
          <div className="grid h-full grid-cols-4 grid-rows-1 gap-3">
            {KANBAN_COLUMNS.map((column) => (
              <KanbanColumn
                column={column}
                jobs={byColumn[column.id]}
                key={column.id}
                onSelect={select}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {KANBAN_COLUMNS.map((column) => {
            const columnJobs = byColumn[column.id];

            return (
              <div className="border-b border-border" key={column.id}>
                <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-surface/95 px-5 py-2 backdrop-blur-sm">
                  <div className="flex items-baseline gap-3">
                    <h3 className="text-sm lg:text-base font-medium">
                      {column.label}
                    </h3>
                    <span className="font-mono text-2xs lg:text-xs tracking-wide text-subtle uppercase">
                      {column.hint}
                    </span>
                  </div>
                  <span className="font-mono text-xs lg:text-sm text-foreground-muted tabular-nums">
                    {columnJobs.length}
                  </span>
                </div>
                {columnJobs.length === 0 ? (
                  <div className="px-5 py-3 font-mono text-2xs lg:text-xs text-subtle">
                    — no entries —
                  </div>
                ) : (
                  columnJobs.map((job) => (
                    <TrackerListRow job={job} key={job.id} onSelect={select} />
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
